import React from 'react';
import { Box, Typography, Paper, alpha, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AttachMoney, CheckCircle, Warning, Assignment } from '@mui/icons-material';
import { formatCurrency, STATUS_CONFIG } from '../constants';
import SummaryCards from '../components/SummaryCards';

/**
 * Activity Summary View - Clean, modern dashboard showing:
 * 1. Project-level status counts and deferred value at top
 * 2. Company-level breakdown below with same metrics
 */
const PeriodActivityView = ({ reportData, filters, showDeferredCost = true }) => {
    const theme = useTheme();

    if (!reportData) return null;

    const { projectStatusSnapshot, companyBreakdowns = [] } = reportData;

    // Status circle component - clean, minimal design
    const StatusCircle = ({ count, label, color }) => (
        <Box sx={{ textAlign: 'center' }}>
            <Box
                sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(color, 0.08),
                    border: `2px solid ${alpha(color, 0.2)}`,
                    mx: 'auto',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        bgcolor: alpha(color, 0.12),
                        borderColor: alpha(color, 0.4),
                    },
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        color: color,
                    }}
                >
                    {count}
                </Typography>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    mt: 1,
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    maxWidth: 80,
                    mx: 'auto',
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    // Deferred value display component
    const DeferredValueDisplay = ({ value, label = 'Deferred Value' }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: { xs: 1.5, sm: 2 },
                bgcolor: alpha(theme.palette.warning.main, 0.06),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                }}
            >
                <AttachMoney sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
            </Box>
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.15rem' },
                        color: 'text.primary',
                    }}
                >
                    {formatCurrency(value)}
                </Typography>
            </Box>
        </Box>
    );

    // Company row component
    const CompanyRow = ({ company }) => {
        const statusCounts = company.statusCounts || {};

        // Calculate company status counts from the map
        const getStatusCount = (statusKey) => {
            const statusMap = {
                submittedCount: 'SUBMITTED',
                acceptedCount: 'ACCEPTED',
                assignedCount: 'ASSIGNED',
                workCompletedCount: 'WORK_COMPLETED',
                internallySignedOffCount: 'INTERNALLY_SIGNED_OFF',
                completedCount: 'COMPLETED',
                rejectedCount: 'REJECTED',
            };
            return statusCounts[statusMap[statusKey]] || 0;
        };

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    '&:hover': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.06)}`,
                    },
                }}
            >
                {/* Top Row: Company Info and Deferred Value */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 0.5,
                            }}
                        >
                            {company.companyName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {company.currentOpenCount} open items
                        </Typography>
                    </Box>

                    {showDeferredCost && (
                        <DeferredValueDisplay
                            value={company.totalDeferredCostOfOpenItems}
                            label="Open Deferred"
                        />
                    )}
                </Box>

                {/* Bottom Row: Status Circles */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        justifyContent: 'space-between', // Spread them out evenly
                        px: { xs: 0, md: 2 },
                    }}
                >
                    {STATUS_CONFIG.map((status) => (
                        <StatusCircle
                            key={status.key}
                            count={getStatusCount(status.key)}
                            label={status.label}
                            color={status.color}
                        />
                    ))}
                </Box>
            </Paper>
        );
    };

    // Filter companies based on selected filter
    const filteredCompanies =
        filters?.assignedToBusinessIds?.length > 0
            ? companyBreakdowns.filter((company) =>
                  filters.assignedToBusinessIds.includes(company.companyId)
              )
            : companyBreakdowns;

    // Sort companies by open count (highest first)
    const sortedCompanies = [...filteredCompanies].sort(
        (a, b) => b.currentOpenCount - a.currentOpenCount
    );

    // Prepare summary cards data
    const summaryCards = [
        {
            title: 'Total Open Items',
            value: projectStatusSnapshot?.openCount || 0,
            icon: Assignment,
            color: 'primary',
        },
        // Deferred Cost card only for deferred-cost-permitted roles (EXT-LAUNCH-009)
        ...(showDeferredCost
            ? [
                  {
                      title: 'Deferred Cost',
                      value: formatCurrency(projectStatusSnapshot?.totalDeferredCost || 0),
                      icon: AttachMoney,
                      color: 'warning',
                  },
              ]
            : []),
        {
            title: 'Completed',
            value: projectStatusSnapshot?.completedCount || 0,
            icon: CheckCircle,
            color: 'success',
        },
        {
            title: 'Rejected',
            value: projectStatusSnapshot?.rejectedCount || 0,
            icon: Warning,
            color: 'error',
        },
    ];

    return (
        <Box>
            {/* Project Summary Cards */}
            <SummaryCards cards={summaryCards} />

            {/* Detailed Status Breakdown */}
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'left' }}>
                    Status Breakdown
                </Typography>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                    }}
                >
                    <Grid container spacing={3} justifyContent="space-between">
                        {STATUS_CONFIG.map((status) => (
                            <Grid key={status.key} size="auto">
                                <StatusCircle
                                    count={projectStatusSnapshot?.[status.key] || 0}
                                    label={status.label}
                                    color={status.color}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>

            {/* Company Breakdown Section */}
            {sortedCompanies.length > 0 && (
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 2.5,
                            color: 'text.primary',
                            textAlign: 'left',
                        }}
                    >
                        By Company
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {sortedCompanies.map((company) => (
                            <CompanyRow key={company.companyId} company={company} />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Empty State */}
            {sortedCompanies.length === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 3,
                        bgcolor: 'transparent',
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        No company data available for this project.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default PeriodActivityView;
